import {
  initDisableCache,
  isDisabledCache,
  setBaseUrl,
} from "../Global/Helper";
import SDKConfigurationModel from "../Configurator/Models/SDKConfiguraitonModel";
import FeatureConfigRemapper from "../Namespaces/Predictor/Models/Config/FeatureConfigRemapper";
import { FeaturesConfigModels } from "../Global/Types/GlobalTypes";
import Https from "./Https";
import { LocalCacheInterface } from "../Global/Interfaces/GlobalInterfaces";
import LocalCacheInstanceFactory from "../Global/Factories/LocalInstanceFactory";
import ListContentFilters from "../Namespaces/Lists/Models/Filters/ListContentFilters";
import ListContentRemapper from "../Namespaces/Lists/Models/ListContentRemapper";

export enum FeatureConfigEnum {
  PREDICTOR = "predictor",
  TOP_X = "topX",
  MATCH_QUIZ = "matchQuiz",
  LOYALTY = "loyalty",
  DISCUSSIONS = "discussions",
  FANTASY = "fantasy",
}

export default class ClientHttps extends Https {
  private featureConfigRemapper: FeatureConfigRemapper = null;
  private listContentRemapper: ListContentRemapper = null;
  private localStorage: LocalCacheInterface = null;
  private clientId: string = null;

  constructor(config: SDKConfigurationModel) {
    super(config, setBaseUrl(config.environment, "client"));
    this.clientId = config.clientId;
    this.localStorage =
      new LocalCacheInstanceFactory().createLocalCacheInstance(config.idSchema);
    this.featureConfigRemapper = new FeatureConfigRemapper();
    this.listContentRemapper = new ListContentRemapper();
  }

  public getConfig = async (
    feature: FeatureConfigEnum
  ): Promise<FeaturesConfigModels> => {
    let clientFeatures = await this.getCachedFeatures();

    if (!clientFeatures) {
      const pathParams = `/clients/${this.clientId}/features`;
      const queryParams = this.apiSignInUrl.split("&")[0]; // We need only api key as a query param for client features
      const url = pathParams + queryParams;
      const warnMessage =
        "There was a problem fetching client features from Client API";
      const data = await this.fetchWithoutAuth(url, warnMessage);

      clientFeatures = data.data;
      await this.localStorage.setClientFeatures(clientFeatures);
    }

    switch (feature) {
      case "predictor":
        return this.featureConfigRemapper.remapPredictorConfig(
          clientFeatures.predictor
        );
      case "topX":
        return this.featureConfigRemapper.remapTopXConfig(clientFeatures.top_x);
      case "matchQuiz":
        return this.featureConfigRemapper.remapMatchQuizConfig(
          clientFeatures.match_quiz
        );
      case "loyalty":
        return this.featureConfigRemapper.remapLoyaltyConfig(
          clientFeatures.loyalty
        );
      case "discussions":
        return this.featureConfigRemapper.remapDiscussionsConfig(
          clientFeatures.discussions
        );
      case "fantasy":
        return this.featureConfigRemapper.remapFantasyConfig(
          clientFeatures.football_fantasy
        );
    }
  };

  public getNewLoyaltyConfig = async (disableCache: boolean) => {
    const pathParams = `/clients/${this.clientId}/features`;
    const queryParams = this.apiSignInUrl.split("&")[0]; // We need only api key as a query param for client features
    const warnMessage =
      "There was a problem fetching client features from Client API";

    if (disableCache) {
      const url = pathParams + queryParams + initDisableCache();
      const data = await this.fetchWithoutAuthAdditionalHeaders(
        url,
        warnMessage,
        {
          "x-api-version": "2",
        }
      );
      await this.localStorage.setClientFeatures(data.data);
    }

    let clientFeatures = await this.getCachedFeatures();

    if (!clientFeatures) {
      const url = pathParams + queryParams;
      const data = await this.fetchWithoutAuthAdditionalHeaders(
        url,
        warnMessage,
        {
          "x-api-version": "2",
        }
      );

      clientFeatures = data.data;
      await this.localStorage.setClientFeatures(clientFeatures);
    }

    return this.featureConfigRemapper.remapLoyaltyNewConfig(
      clientFeatures.loyalty
    );
  };

  public getListContent = async (
    listId: string,
    filters: ListContentFilters,
    disableCache: boolean
  ) => {
    let url = `/clients/${this.clientId}/lists/${listId}/content${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructFiltersUrl();
    }

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem fetching client list content";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.listContentRemapper.remapListContent(data);
  };

  private getCachedFeatures = async () => {
    const clientFeatures = await this.getLocalStorageData();

    if (!clientFeatures || !Object.keys(clientFeatures).length) return;

    // When clientId has been changed (a new config is provided to SDK), we need to refetch the features.
    if (
      clientFeatures.predictor.client_id &&
      clientFeatures.predictor.client_id !== this.clientId
    ) {
      await this.localStorage.deleteClientFeatures();

      return null;
    }

    return clientFeatures;
  };

  private getLocalStorageData = async () => {
    const now = Math.floor(Date.now() / 1000);
    const expiration = await this.localStorage.getExpiration("client", "features");

    if (expiration > now) {
      return await this.localStorage.getClientFeatures();
    }

    return null;
  };
}
